import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Alert } from '@mui/material';
import { useUser } from '../contexts/UserContext';
import { usePageContent } from '../contexts/PageContentContext';
import { playerService, FantasyTeamGameScheduleDto, FantasyTeam } from '../services/playerService';

const SchedulePage: React.FC = () => {
  const { currentUsername, isUserSwitching } = useUser();
  const { setPageState } = usePageContent();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [games, setGames] = useState<FantasyTeamGameScheduleDto[]>([]);

  useEffect(() => {
    const load = async () => {
      if (!currentUsername) return;
      try {
        setLoading(true);
        setError(null);

        const user = await playerService.getUserByUsername(currentUsername);
        const teams: FantasyTeam[] = await playerService.getFantasyTeamsByUser(user.id);

        // Fetch schedules per team to avoid heavy navigation data
        const teamGamesArrays = await Promise.all(
          teams.map(async (team) => {
            try {
              return await playerService.getFantasyTeamGamesByTeam(team.id);
            } catch (e) {
              console.warn(`SchedulePage: failed to load games for team ${team.id}`, e);
              return [] as FantasyTeamGameScheduleDto[];
            }
          })
        );

        const combined = teamGamesArrays.flat();
        combined.sort((a, b) => a.roundStart - b.roundStart || a.id - b.id);
        setGames(combined);
        
        // Signal page state: has content if games exist, no error, not loading
        setPageState({
          hasContent: combined.length > 0,
          isLoading: false,
          hasError: false,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load schedule');
        setPageState({
          hasContent: false,
          isLoading: false,
          hasError: true,
        });
      } finally {
        setLoading(false);
      }
    };
    if (!isUserSwitching) {
      load();
    }
  }, [currentUsername, isUserSwitching]);

  // Update page state for loading/error states
  useEffect(() => {
    if (loading || isUserSwitching) {
      setPageState({ isLoading: true, hasContent: false, hasError: false });
    } else if (error) {
      setPageState({ isLoading: false, hasContent: false, hasError: true });
    } else if (games.length === 0) {
      // Empty state - no content
      setPageState({ isLoading: false, hasContent: false, hasError: false });
    }
  }, [loading, error, games.length, isUserSwitching, setPageState]);

  if (loading || isUserSwitching) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading schedule...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>My Schedule</Typography>
          {games.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No scheduled games found.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Round Start</TableCell>
                  <TableCell>Round End</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>Opponent</TableCell>
                  <TableCell align="right">My Score</TableCell>
                  <TableCell align="right">Opponent Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {games.map(g => (
                  <TableRow key={g.id}>
                    <TableCell>{g.roundStart}</TableCell>
                    <TableCell>{g.roundEnd}</TableCell>
                    <TableCell>{g.fantasyTeamName ?? g.fantasyTeamId}</TableCell>
                    <TableCell>{g.opponentFantasyTeamName ?? g.opponentFantasyTeamId ?? 'TBD'}</TableCell>
                    <TableCell align="right">{g.totalFantasyPoints ?? 0}</TableCell>
                    <TableCell align="right">{g.opponentFantasyTeamScore ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SchedulePage;

